import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import ApiService from "../../services/api-service";
import VideoApiService from "../../services/video-api-service";
import NoData from "../../components/Nodata";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

export default function SimpleVideoCarousel() {
  const { t } = useTranslation("common");
  const history = useHistory();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch videos data
  const fetchVideos = async () => {
    try {
      setLoading(true);
      // Use the featured videos endpoint instead of user-specific videos
      const response = await ApiService.FEATURE_VIDEO('pageSize=10&pageNo=1');
      if (response && response.result && response.result.length > 0) {
        setVideos(response.result);
      } else {
        setVideos([]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching videos:", error);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []); // Only fetch once on mount since we're getting featured videos

  const handleVideoClick = (video) => {
    
    try {
      if (!video._id) {
        return;
      }

      // Extract playlist ID from video/thumbnail URLs
      let playlistId = null;
      
      // Try to extract from video URL first
      if (video.video?.url) {
        const videoUrlMatch = video.video.url.match(/\/([a-f0-9]{24})\//);
        if (videoUrlMatch) {
          playlistId = videoUrlMatch[1];
        }
      }
      
      // If not found, try thumbnail URL
      if (!playlistId && video.thumbnail?.url) {
        const thumbnailUrlMatch = video.thumbnail.url.match(/\/([a-f0-9]{24})\//);
        if (thumbnailUrlMatch) {
          playlistId = thumbnailUrlMatch[1];
        }
      }
      
      // If still not found, try direct studioId field (if it's a string)
      if (!playlistId && video.studioId && typeof video.studioId === 'string') {
        playlistId = video.studioId;
      }
      
      // If we found a playlist ID, navigate to it
      if (playlistId && video._id) {
        history.push(`/app/video-player/${playlistId}/${video._id}`);
      } else {
        
        // Fallback: try using the uploader ID from the original video data
        let uploaderStudioId = video.uploader?._id || video.uploader;
        if (uploaderStudioId) {
          history.push(`/app/video-player/${uploaderStudioId}/${video._id}`);
        }
      }
    } catch (error) {
      
      // Fallback: try using the uploader ID from the original video data
      let uploaderStudioId = video.uploader?._id || video.uploader;
      if (uploaderStudioId) {
        history.push(`/app/video-player/${uploaderStudioId}/${video._id}`);
      }
    }
  };
  

  return (
    <Box
      sx={{
        width: "100%",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // backgroundColor: (theme) => theme.palette.background.paper,
        // borderRadius: "12px",
        // boxShadow: (theme) => theme.shadows[1],
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: (theme) => theme.palette.text.primary,
          fontWeight: "bold",
          fontFamily: "Inter, sans-serif",
          alignSelf: "flex-start",
          marginBottom: "10px",
          fontSize: "16px",
        }}
      >
        {t("carousel.title", { defaultValue: "Featured Videos" })}
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: "15px",
          overflowX: "auto",
          padding: "5px",
          width: "100%",
          alignItems: "flex-start",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {loading ? (
          <Loader />
        ) : videos.length > 0 ? (
          videos.map((video) => (
            <Box
              key={video._id}
              sx={{
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: "8px", // Reduced gap for better spacing
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 4px 12px rgba(255, 255, 255, 0.2)",
                },
              }}
              onClick={() => handleVideoClick(video)}
            >
              <Box
                sx={{
                  width: "203px",
                  height: "114px", // Changed to 16:9 aspect ratio (203 * 9/16 = 114)
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.action.hover
                }}
              >
                <img
                  src={video.thumbnail?.url || video.thumbnailUrl || video.thumbnail}
                  alt={video.title || video.name || 'Video'}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover", // Changed from "contain" to "cover" for better fill
                    display: "block"
                  }}
                />
              </Box>
              <Box sx={{ maxWidth: "203px", padding: "0" }}> {/* Removed padding for tighter spacing */}
                <Typography
                  variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: 1.2,
                    marginBottom: "1px", // Reduced margin
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {video.title || video.name || 'Untitled Video'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                  color: (theme) => theme.palette.text.secondary,
                    fontSize: "10px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {video.playlistName || video.studioName || (video.uploader?.firstName ? `${video.uploader.firstName} ${video.uploader.lastName || ''}`.trim() : 'Unknown Artist')}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <NoData />
        )}
      </Box>
    </Box>
  );
}
